// lib
import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext } from '@aytm/app/stats/page/question-views/xq/Component'

import { encodeText, decodeText } from '@aytm/app/stats/lib/helpers'

// components
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'

// styles
import style from '@aytm/app/stats/page/question-views/xq/styles/ScriptItem.module.scss'

const MODE_TEXTS = {
  advanced: 'switch to basic mode',
  basic: 'switch to advanced mode',
  basicDisabled: 'simplify/clear logic to switch to the basic mode'
}
const TEXT_WIDTH = 448
const TEXT_VPADDING = 9
const TEXT_FONT_SIZE = 14
const TEXT_LINE_HEIGHT = 16
const TEXT_MIN_HEIGHT = 34
const TEXT_MAX_HEIGHT = 80

const onScriptItemValueKeyDown = event => 13 == event.keyCode && event.preventDefault()

const ScriptItemText = ({ item, value, basic }) => {
  const { model, disabled, onChange } = useContext(DataContext)

  const [height, setHeight] = useState(TEXT_MIN_HEIGHT)
  const [minHeight, setMinHeight] = useState(TEXT_MIN_HEIGHT)

  const [mode, setMode] = useState('advanced')

  const [error, setError] = useState(false)
  const [basicDisabled, setBasicDisabled] = useState(false)

  const editRef = useRef(false)
  const callbackRef = useRef(null)

  const basicOptions = useMemo(
    () =>
      basic && 0 < basic.length
        ? basic.map(r => {
            return { text: r.text, key: r.value }
          })
        : null,
    [basic]
  )

  const onBasicChange = useCallback(
    option => {
      if (value == option) return

      model.args[item.id] = option
      onChange(model)
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValueChange = useCallback(
    event => {
      const text = encodeText(event.target.value)
      if (value == text) return

      model.args[item.id] = text
      onChange(model)
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValuePaste = useCallback(
    event => {
      event.preventDefault()

      let text =
        (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
      text = text.replace(/(\r\n|\n|\r|\t|\v)/gm, ' ')

      const el = event.currentTarget

      const l = text.length
      const start = el.selectionStart
      const end = el.selectionEnd

      const valueBefore = el.value.slice(0, start)
      const valueAfter = el.value.slice(end)
      text = encodeText(`${valueBefore}${text}${valueAfter}`)

      if (value != text) {
        model.args[item.id] = text
        onChange(model)
      }

      callbackRef.current = () => {
        el.setSelectionRange(start + l, start + l)
        el.blur()
        el.focus()
      }
    },
    [item.id, value, model, onChange]
  )

  const onScriptItemValueFocus = () => (editRef.current = true)

  const onScriptItemValueBlur = useCallback(() => {
    editRef.current = true

    setError(item.required && !value.trim().length)
  }, [item.required, value])

  useEffect(() => {
    if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
      callbackRef.current()
      callbackRef.current = null
    }
  }, [value])

  useEffect(() => {
    const basicDisabled = 0 < value.length && basic && 0 < basic.length && !basic.some(r => r.value == value)

    setMode(basicDisabled || !basic || !basic.length ? 'advanced' : mode)
    setBasicDisabled(basicDisabled)

    editRef.current && setError(item.required && !value.trim().length)
  }, [item.required, value, basic, mode])

  useEffect(() => {
    const div = document.createElement('div')

    document.body.appendChild(div)

    div.style.position = 'absolute'
    div.style.left = '-1000px'
    div.style.top = '-1000px'
    div.style.width = `${TEXT_WIDTH}px`
    div.style.fontSize = `${TEXT_FONT_SIZE}px`
    div.style.lineHeight = `${TEXT_LINE_HEIGHT}px`

    div.style.overflow = 'hidden'
    div.style['overflow-wrap'] = 'break-word'

    div.innerHTML = value || '&nbsp;'
    setHeight(Math.min(div.clientHeight + 2 * TEXT_VPADDING, TEXT_MAX_HEIGHT))

    div.innerHTML = item.default || item.placeholder || '&nbsp;'
    setMinHeight(Math.min(div.clientHeight + 2 * TEXT_VPADDING, TEXT_MAX_HEIGHT))

    div.parentNode.removeChild(div)
  }, [item.default, item.placeholder, value])

  const renderAdvancedMode = () => (
    <InputTextarea
      className={error ? 'p-invalid' : ''}
      value={decodeText(value)}
      placeholder={item.placeholder || ''}
      rows={1}
      autoResize={true}
      onChange={onScriptItemValueChange}
      onKeyDown={onScriptItemValueKeyDown}
      onPaste={onScriptItemValuePaste}
      onFocus={onScriptItemValueFocus}
      onBlur={onScriptItemValueBlur}
      style={{
        height,
        minHeight,
        maxHeight: TEXT_MAX_HEIGHT
      }}
    />
  )

  const renderBasicMode = () => (
    <Dropdown
      className={error ? 'p-invalid' : ''}
      placeholder=""
      value={value}
      options={basicOptions}
      optionValue="key"
      optionLabel="text"
      fixedDropdownDir="bottom"
      onChange={event => onBasicChange(event.value)}
    />
  )

  return (
    <div className={`${style.item} p-fluid`}>
      {disabled ? (
        <div className="p-field p-col-12" dangerouslySetInnerHTML={{ __html: value }} />
      ) : (
        <>
          <div
            className={`${style['item-title']} p-field p-col-4`}
            dangerouslySetInnerHTML={{ __html: item.text || item.id }}
          />
          <div className={`${style['item-component']} p-field p-col-8`}>
            <span className={style['item-note']}>Type: {item.type}</span>
            {basic && 0 < basic.length ? (
              <span
                className={classNames(style['item-toggle'], {
                  [style['item-toggle-disabled']]: basicDisabled
                })}
                onClick={basicDisabled ? null : () => setMode('basic' == mode ? 'advanced' : 'basic')}
              >
                {MODE_TEXTS[basicDisabled ? 'basicDisabled' : mode]}
              </span>
            ) : null}
            {'advanced' == mode ? renderAdvancedMode() : renderBasicMode()}
          </div>
        </>
      )}
    </div>
  )
}

ScriptItemText.propTypes = {
  item: PropTypes.object,
  value: PropTypes.any,
  basic: PropTypes.array
}

ScriptItemText.displayName = 'ScriptItemText'

export default ScriptItemText
